import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../design-system/tokens";
import { listInstances } from "../../../service/sshProxyService";
import { endpointDetailsPath } from "../../../components/privilegedAccessManagement/endpoints/endpointLinks";

// Shared pieces of the Password Rotation page. The limits and the account-name
// rule mirror internal/pam/passwords/policy.go (normalise, rotatable).

export const PASSWORD_HELP =
  "Local passwords on your endpoints are random and rotated automatically. Nobody needs to know them: the gateway logs in for approved sessions, and direct password SSH is blocked except for accounts you allow. Reveal a password only for break-glass at the machine's console. Every reveal needs your approval on your phone and is followed by an automatic rotation.";

export const PROFILE_LIMITS = {
  nameMax: 120,
  intervalMin: 1,
  intervalMax: 365,
  intervalDefault: 30,
  lengthMin: 12,
  lengthMax: 128,
  lengthDefault: 24,
  rerotateMin: 5,
  rerotateMax: 1440,
  rerotateDefault: 60,
};

export const RESERVED_ACCOUNTS = ["root", "authnull-svc", "nobody"];

// Why an account name can't go in a profile, or "" when it can.
export function accountNameProblem(a) {
  const s = String(a || "");
  if (!s) return "Empty name.";
  if (s === "root") return "root is chosen with “Include root”.";
  if (RESERVED_ACCOUNTS.includes(s)) return `${s} can't be rotated.`;
  if (s.length > 32) return `${s}: at most 32 characters.`;
  if (s[0] === "-") return `${s}: can't start with '-'.`;
  if (!/^[A-Za-z0-9._-]+$/.test(s)) return `${s}: letters, digits, '.', '_' or '-' only.`;
  return "";
}

export const LOGIN_LABEL = {
  password: "Gateway logs in with password",
  none: "Rotation only",
};

export const muted = { fontSize: 11.5, color: AN_COLORS.muted };
export const target = (r) => `${r.account || "?"}@${r.hostname || `endpoint #${r.endpointId}`}`;

// "24 · A a 0 #": the length, then each character class the profile uses.
export function shapeLabel(p) {
  const classes = [p.upper && "A", p.lower && "a", p.digits && "0", p.special && "#"].filter(Boolean).join(" ");
  return `${p.length} · ${classes || "—"}`;
}

// Opens an endpoint's Gateway Access tab. The details page loads the endpoint
// named by ?id=; the row is checked here first so a missing one is reported in
// place, and stored for older links that carry no id.
export function useOpenEndpointSetup(messageApi) {
  const navigate = useNavigate();
  return useCallback(
    (endpointId) =>
      listInstances({
        onSuccess: (rows) => {
          const r = rows.find((x) => String(x.instanceId) === String(endpointId));
          if (!r) {
            messageApi.error("Couldn't find that endpoint.");
            return;
          }
          localStorage.setItem("endPoints", JSON.stringify(r));
          localStorage.removeItem("details");
          localStorage.setItem("details", JSON.stringify({ hostAddress: r.hostName, instanceId: r.instanceId }));
          navigate(endpointDetailsPath(r.instanceId, "defaultTab=gateway"));
        },
        onFailure: () => messageApi.error("Couldn't load the endpoint."),
      }),
    [navigate, messageApi],
  );
}

export function SetupChip({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, padding: "1px 8px",
        borderRadius: 99, border: "1px solid var(--an-warnBd)", background: "var(--an-warnBg)", color: AN_COLORS.warn,
        fontFamily: AN_FONT, fontSize: 11, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap",
      }}
    >
      <AlertTriangle size={11} />
      Endpoint needs Re-run setup
    </button>
  );
}

export const monoText = { fontFamily: AN_MONO, fontSize: 12, color: AN_COLORS.ink2 };
